/* eslint-disable no-extra-parens */
import { baseClasses } from '@weatherbug/common';

import { TOGGLE_DAILY_CARD_SUMMARY_DETAILS, ToggleExpandDailySummaryDetailsActionType } from '../actions/dailyForecastPageViewActions';
import type { DailyForecastPeriod, DayNightPeriods, ExpandedDailyCardsRecordType, DailyForecastPeriodFromServer, DailyForecastPeriodsFromServer, TransformedServerResponseForClient } from '../../components/forecasts/common/types';

const { AsyncAction } = baseClasses;

export const GET_DAILY_FORECAST_BY_LATLON = new AsyncAction('GET_DAILY_FORECAST_BY_LATLON');

export type StateType = {
    expandedDailyCardsRecords: ExpandedDailyCardsRecordType;
    forecastData: {
        dailyForecastPeriods?: DayNightPeriods[];
        error: any;
        dailyForecastIsRefreshing: boolean;
    };
    chartData: {
        minTemp: number;
        maxTemp: number;
    };
}

const transformServerDailyForecastToClientInitialData = (serverDailyForecastPeriods: DailyForecastPeriodsFromServer[]): TransformedServerResponseForClient => {
    if (Array.isArray(serverDailyForecastPeriods) && serverDailyForecastPeriods?.length > 0) {
        let maxTemp = -Infinity;
        let minTemp = Infinity;

        const clientInitialDailyForecastPeriods = serverDailyForecastPeriods.map(serverDailyForecastPeriod => {
            const dayPeriod = serverDailyForecastPeriod?.Day ? {
                forecastDateLocalStr: serverDailyForecastPeriod.Day.ForecastDateLocalStr,
                forecastDateUtcStr: serverDailyForecastPeriod.Day.ForecastDateUtcStr,
                iconCode: serverDailyForecastPeriod.Day.IconCode,
                isNightTimePeriod: serverDailyForecastPeriod.Day.IsNightTimePeriod,
                temperature: serverDailyForecastPeriod.Day.Temperature,
                detailedDescription: serverDailyForecastPeriod.Day.DetailedDescription,
                summaryDescription: serverDailyForecastPeriod.Day.SummaryDescription
            } : null;

            const nightPeriod = {
                forecastDateLocalStr: serverDailyForecastPeriod?.Night?.ForecastDateLocalStr,
                forecastDateUtcStr: serverDailyForecastPeriod?.Night?.ForecastDateUtcStr,
                iconCode: serverDailyForecastPeriod?.Night?.IconCode,
                isNightTimePeriod: serverDailyForecastPeriod?.Night?.IsNightTimePeriod,
                temperature: serverDailyForecastPeriod?.Night?.Temperature,
                detailedDescription: serverDailyForecastPeriod?.Night?.DetailedDescription,
                summaryDescription: serverDailyForecastPeriod?.Night?.SummaryDescription
            };

            if (Number.isFinite(nightPeriod?.temperature)) {
                minTemp = Math.min(minTemp, Number(nightPeriod.temperature));
                maxTemp = Math.max(maxTemp, Number(nightPeriod.temperature));
            }

            if (Number.isFinite(dayPeriod?.temperature)) {
                minTemp = Math.min(minTemp, Number(dayPeriod!.temperature));
                maxTemp = Math.max(maxTemp, Number(dayPeriod!.temperature));
            }
            return {
                day: dayPeriod,
                night: nightPeriod
            };
        });
        
        // eslint-disable-next-line dot-notation
        (window as any)._AppData['shouldUseServerDailyForecastData'] = true;

        return {
            forecastDaily: clientInitialDailyForecastPeriods,
            chart: {
                minTemp: Number.isFinite(minTemp) ? minTemp - 3 : 0,
                maxTemp: Number.isFinite(maxTemp) ? maxTemp : 0
            }
        };
    }
    return {
        forecastDaily: [],
        chart: {
            minTemp:  0,
            maxTemp:  0
        }
    };
};

const initialPageLoadDailyForecastPeriods = transformServerDailyForecastToClientInitialData((window as any)._AppData?.DailyForecasts?.[0]);

const initialState: StateType = {
    expandedDailyCardsRecords: {},
    forecastData: {
        dailyForecastPeriods: initialPageLoadDailyForecastPeriods.forecastDaily,
        dailyForecastIsRefreshing: false,
        error: undefined
    },
    chartData: initialPageLoadDailyForecastPeriods.chart
};

const isValidTemp = (temp?: string | number): boolean => {
    if (temp === undefined || temp === null || typeof temp === 'string' && temp?.length === 0) {
        return false;
    }
    return true;
};

type dailyForecastPageActionTypes = ToggleExpandDailySummaryDetailsActionType;

export function dailyForecastPage(state = initialState, action: dailyForecastPageActionTypes): StateType
{
    switch (action.type)
    {
        case TOGGLE_DAILY_CARD_SUMMARY_DETAILS:
        {
            const { expandedDailyCardsRecords } = state;
            const dayCardIndex  = action.payload;
            
            const updateExpandedDailyCardsRecord = { ...expandedDailyCardsRecords };
            if (updateExpandedDailyCardsRecord[dayCardIndex]) {
                updateExpandedDailyCardsRecord[dayCardIndex] = !expandedDailyCardsRecords[dayCardIndex];
            } else {
                updateExpandedDailyCardsRecord[dayCardIndex] = true;
            }
            
            return {
                ...state,
                expandedDailyCardsRecords: updateExpandedDailyCardsRecord
            };
        }

        case GET_DAILY_FORECAST_BY_LATLON.pending:
        {
            return {
                ...state,
                forecastData: {
                    dailyForecastIsRefreshing: true,
                    error: undefined,
                    dailyForecastPeriods: []
                }
            };
        }

        case GET_DAILY_FORECAST_BY_LATLON.fulfilled:
        {
            const dailyForecastPeriods = action?.payload?.dfp;
            const formattedDailyForecastPeriods: DayNightPeriods[] = [];
            let index = 0;
            let modulate = 0;
            let maxTemp = -Infinity;
            let minTemp = Infinity;
            
            if (dailyForecastPeriods?.[0]?.intp) {
                const day = null;
                const nightFromPayload = { ...dailyForecastPeriods[0] };
                const night: DailyForecastPeriod = {
                    forecastDateLocalStr: nightFromPayload.fdls,
                    forecastDateUtcStr: nightFromPayload.fdus,
                    iconCode: nightFromPayload.ic,
                    isNightTimePeriod: nightFromPayload.intp,
                    temperature: nightFromPayload.t,
                    detailedDescription: nightFromPayload.dd,
                    summaryDescription: nightFromPayload.sd
                };
                index++;
                modulate = 1;
                formattedDailyForecastPeriods.push({
                    day,
                    night
                });
                if (isValidTemp(night?.temperature)) {
                    minTemp = Math.min(minTemp, Number(night?.temperature));
                    maxTemp = Math.max(maxTemp, Number(night?.temperature));   
                }
            }

            for (; index < dailyForecastPeriods?.length; index++) {
                const chartPeriod = dailyForecastPeriods[index];
                if (isValidTemp(chartPeriod?.t)) {
                    minTemp = Math.min(minTemp, Number(chartPeriod?.t));
                    maxTemp = Math.max(maxTemp, Number(chartPeriod?.t));  
                }

                if (index % 2 === modulate) {
                    const dayNightPeriod: DayNightPeriods = {
                        day: null,
                        night: null
                    };
                    const dayPeriod = { ...dailyForecastPeriods[index + 0] };
                    dayNightPeriod.day = {
                        forecastDateLocalStr: dayPeriod.fdls,
                        forecastDateUtcStr: dayPeriod.fdus,
                        iconCode: dayPeriod.ic,
                        isNightTimePeriod: dayPeriod.intp,
                        temperature: dayPeriod.t,
                        detailedDescription: dayPeriod.dd,
                        summaryDescription: dayPeriod.sd
                    };

                    if (index + 1 < dailyForecastPeriods.length)
                    {
                        const nightPeriod = { ...dailyForecastPeriods[index + 1] };
                        dayNightPeriod.night = {
                            forecastDateLocalStr: nightPeriod.fdls,
                            forecastDateUtcStr: nightPeriod.fdus,
                            iconCode: nightPeriod.ic,
                            isNightTimePeriod: nightPeriod.intp,
                            temperature: nightPeriod.t,
                            detailedDescription: nightPeriod.dd,
                            summaryDescription: nightPeriod.sd
                        };
                    }
                    formattedDailyForecastPeriods.push(dayNightPeriod);
                }
                
            }

            minTemp -= 3;

            return {
                ...state,
                forecastData: {
                    dailyForecastIsRefreshing: false,
                    error: undefined,
                    dailyForecastPeriods: formattedDailyForecastPeriods
                },
                chartData: {
                    minTemp,
                    maxTemp
                }
            };  
        }
        case GET_DAILY_FORECAST_BY_LATLON.rejected:
        {
            return {
                ...state,
                forecastData: {
                    dailyForecastIsRefreshing: false,
                    error: action.payload,
                    dailyForecastPeriods: []
                }
            };
        }
        default: 
        {
            return state;
        }
    }
}